import React, { useEffect, useRef, useState } from "react";

interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (item: T, index: number) => React.ReactNode;
  isHtml?: boolean;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  rowKey?: (item: T, index: number) => React.Key;
  className?: string;
  mobileLimitPerGroup?: number;
  emptyMessage?: string;
  theme?: "auto" | "light" | "dark";
}

function PartialOrderTable<T>({
  data,
  columns,
  rowKey = (_, index) => index,
  className = "",
  mobileLimitPerGroup = 5,
  emptyMessage = "No records found",
  theme = "auto",
}: DataTableProps<T>): JSX.Element {
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767.98px)").matches
      : false
  );

  // tooltip DOM element ref (appended to body)
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const fadeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const styleId = "partial-order-table-responsive-styles";
    if (!document.getElementById(styleId)) {
      const css = `
        :root { --pot-bg: #ffffff; --pot-surface: #f8fafc; --pot-text: #0f172a; --pot-muted: #6b7280; --pot-border: rgba(15,23,42,0.06); --pot-accent: #2563eb; --pot-badge-text: #fff; }
        [data-theme="dark"] { --pot-bg: #071022; --pot-surface: #0b1117; --pot-text: #e6eef8; --pot-muted: #9aa6b6; --pot-border: rgba(255,255,255,0.04); --pot-accent: #1ea7ff; --pot-badge-text: #061217; }
        .partial-order-table-root, .pot-root { box-sizing: border-box;   background: #171724,      color:var(--pot-text); font-family: Inter, Roboto, system-ui, -apple-system, 'Segoe UI', sans-serif; }
        .pot-table-wrap{ overflow:auto; border-radius:8px; border:1px solid var(--pot-border); padding:6px; }
        table.pot-table{ width:100%; border-collapse:collapse; font-size:0.95rem; color:var(--pot-text); }
        table.pot-table thead th{ text-align:left; padding:10px 12px; font-weight:600; font-size:0.82rem; border-bottom:1px solid var(--pot-border); }
        table.pot-table td{ padding:10px 12px; border-bottom:1px solid var(--pot-border); vertical-align:middle; max-width:320px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .pot-group-header td{ background: transparent; padding:8px 12px; }
        .pot-badges{ display:flex; gap:8px; align-items:center; flex-wrap:wrap; max-width:100%; overflow:hidden; }
        .pot-badge{ padding:2px 3px; border-radius:999px; font-weight:600; font-size:0.82rem; color:var(--pot-badge-text); white-space:nowrap; }
        .pot-action-cell{ display:flex; gap:8px;}
        .pot-card-list{ display:flex; flex-direction:column; gap:12px; padding:6px 0; }
        .pot-card{border:1px solid var(--pot-border); border-radius:10px; padding:10px; box-shadow: 0 1px 0 rgba(0,0,0,0.03); min-width:0; overflow:hidden; display:flex; flex-direction:column; gap:10px; }
        .pot-card-head{ display:flex; gap:12px; align-items:center; justify-content:space-between; min-width:0; }
        .pot-thumb{ width:56px; height:56px; border-radius:8px; overflow:hidden; background:transparent; border:1px solid var(--pot-border); flex:0 0 56px; }
        .pot-card-title{ font-weight:700; font-size:0.95rem; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .pot-card-sub{ font-size:0.86rem; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .pot-card-rows{ display:flex; flex-direction:column; gap:8px; }
        .pot-row { display:flex; justify-content:space-between; gap:12px; align-items:center; }
        .pot-row-label { font-weight:600; font-size:0.85rem; min-width:0; }
        .pot-row-value { font-size:0.9rem; color:var(--pot-text); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; min-width:0; }
        .pot-rcfactor { display:flex; gap:12px; align-items:center; margin:6px 0; }
        .pot-rcfactor > div { flex: 1 1 50%; min-width:0; }
        .pot-rc-label{ font-size:13px; font-weight:700; }
        .pot-rc-value{ font-size:14px; color:var(--pot-text); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .pot-empty { padding:18px; text-align:center; }
        .pot-badges, .group-header-badges { display:flex; gap:8px; align-items:center; flex-wrap:wrap; max-width:100%; overflow:hidden; }
        @media (max-width: 420px) {
          .pot-badges, .group-header-badges { white-space: nowrap; flex-wrap: nowrap; overflow-x: auto; -webkit-overflow-scrolling: touch; padding-bottom: 6px; }
          .pot-badges::-webkit-scrollbar, .group-header-badges::-webkit-scrollbar { height: 6px; }
          .pot-badges::-webkit-scrollbar-thumb, .group-header-badges::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 6px; }
        }
        .pot-card-head > div:first-child, .rt-card-head > .card-left { display:flex; gap:12px; align-items:center; min-width:0; flex:1 1 auto; }
        .pot-card-title, .pot-card-sub, .card-title, .card-sub, .rt-card-value { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        @media (max-width: 360px) {
          .pot-card, .rt-card { padding:8px; }
          .pot-thumb, .rt-thumb { width:48px; height:48px; flex:0 0 48px; }
          .pot-badge { font-size:0.72rem; padding:3px 5px; }
        }
        @media (max-width: 767.98px) {
          table.pot-table thead { display:none; }
          table.pot-table tbody tr { display:block; margin-bottom:12px; border-radius:10px; border:1px solid var(--pot-border); padding:10px; }
          table.pot-table tbody td { display:flex; justify-content:space-between; padding:8px; border-bottom:none; }
          table.pot-table tbody .pot-actions-mobile { display:flex; gap:8px; justify-content:flex-end; }
        }

        /* tooltip body styles (the visible tooltip node appended to body) */
        .pot-tooltip-body {
          position: fixed;
          pointer-events: none;
          background: var(--pot-text);
          color: var(--pot-bg);
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 13px;
          line-height: 1.2;
          max-width: 420px;
          box-shadow: 0 6px 24px rgba(2,6,23,0.24);
          opacity: 0;
          transform: translateY(6px);
          transition: opacity 0.12s ease, transform 0.12s ease;
          z-index: 99999;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .pot-tooltip-body.show { opacity: 1; transform: translateY(0); pointer-events: auto; }
      `;
      const style = document.createElement("style");
      style.id = styleId;
      style.appendChild(document.createTextNode(css));
      document.head.appendChild(style);
    }

    // create tooltip DOM node appended to body
    const existing = document.getElementById("pot-tooltip-body");
    if (!existing) {
      const t = document.createElement("div");
      t.id = "pot-tooltip-body";
      t.className = "pot-tooltip-body";
      t.setAttribute("role", "tooltip");
      document.body.appendChild(t);
      tooltipRef.current = t;
    } else {
      tooltipRef.current = existing as HTMLDivElement;
    }

    // screen watcher
    const mq = window.matchMedia("(max-width: 767.98px)");
    const handler = (ev: MediaQueryListEvent | MediaQueryList) => {
      const m = "matches" in ev ? ev.matches : mq.matches;
      setIsMobile(m);
    };
    handler(mq);
    if (mq.addEventListener) mq.addEventListener("change", handler as any);
    else mq.addListener(handler as any);

    return () => {
      // cleanup tooltip node
      try {
        const node = document.getElementById("pot-tooltip-body");
        if (node && node.parentElement) node.parentElement.removeChild(node);
      } catch {}
      if (mq.removeEventListener)
        mq.removeEventListener("change", handler as any);
      else mq.removeListener(handler as any);
      if (fadeTimeoutRef.current) {
        window.clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
    };
  }, [theme]);

  // show tooltip content near target rect
  const showTooltip = (text: string, rect: DOMRect, wrap = false) => {
    const el = tooltipRef.current;
    if (!el) return;
    el.textContent = text;
    if (wrap) el.style.whiteSpace = "normal";
    else el.style.whiteSpace = "pre";
    el.classList.add("show");

    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxW = Math.min(420, vw - 16);
    el.style.maxWidth = `${maxW}px`;

    requestAnimationFrame(() => {
      const tRect = el.getBoundingClientRect();
      let left = rect.left + rect.width / 2 - tRect.width / 2;
      left = Math.max(8, Math.min(left, vw - tRect.width - 8));
      let top = rect.top - tRect.height - margin;
      if (top < 8) {
        top = rect.bottom + margin;
      }
      top = Math.max(8, Math.min(top, vh - tRect.height - 8));
      el.style.left = `${Math.round(left)}px`;
      el.style.top = `${Math.round(top)}px`;
    });

    if (fadeTimeoutRef.current) {
      window.clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }
  };

  const hideTooltip = (delay = 60) => {
    const el = tooltipRef.current;
    if (!el) return;
    if (fadeTimeoutRef.current) window.clearTimeout(fadeTimeoutRef.current);
    fadeTimeoutRef.current = window.setTimeout(() => {
      el.classList.remove("show");
      fadeTimeoutRef.current = null;
    }, delay);
  };

  // Helpers (unchanged logic)
  const getNestedValue = (obj: any, path: string): any =>
    path
      .split(".")
      .reduce(
        (acc, key) => (acc && typeof acc === "object" ? acc[key] : undefined),
        obj
      );

  const renderCellValue = (col: Column<T>, item: T, rowIndex: number) => {
    const raw = col.render
      ? col.render(item, rowIndex)
      : getNestedValue(item, col.key.toString());
    if (raw === undefined || raw === null) return "";
    if (React.isValidElement(raw)) return raw;
    return raw;
  };

  const isImageUrl = (v: any) => {
    if (!v || typeof v !== "string") return false;
    const lower = v.toLowerCase();
    return (
      /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/.test(lower) ||
      lower.startsWith("http://") ||
      lower.startsWith("https://") ||
      lower.startsWith("/storage/")
    );
  };

  const isSerialLabel = (label: string) =>
    /(^|\s)(sr\.?\s*no|serial\s*no|s\.?r\.?\s*no)(\s|$)/i.test(label.trim());

  const groupData = (items: T[]) => {
    const groups: Record<string, T[]> = {};
    (items || []).forEach((item: any) => {
      const key = `${item.company_name || "Unknown"}|${
        item.customer_order_no || "N/A"
      }|${item.unique_partial_order_no || "N/A"}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  };

  const grouped = groupData(data);
  const groupKeys = Object.keys(grouped);

  const actionColIndex = columns.findIndex((c) =>
    c.label.toLowerCase().includes("action")
  );
  const actionCol = actionColIndex >= 0 ? columns[actionColIndex] : undefined;

  const visibleColumns = columns.filter(
    (col, idx) =>
      ![
        "company_name",
        "customer_order_no",
        "unique_partial_order_no  ",
        "action",
      ].includes(col.key.toString())
  );

  // Desktop view (attach show/hide handlers to tooltip-elements)
  const DesktopView = () => (
    <div
      className={`pot-root ${className}`}
      data-theme={theme === "auto" ? undefined : theme}
    >
      <div className="pot-table-wrap" role="table" aria-label="Grouped table">
        <table className="pot-table" role="presentation">
          <thead>
            <tr>
              {visibleColumns.map((col, i) => (
                <th key={`h-${i}`}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groupKeys.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + (actionCol ? 1 : 0)}
                  className="pot-empty"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              groupKeys.map((gk, gi) => {
                const rows = grouped[gk] || [];
                const [company, orderNo, uniqueQuotation] = gk.split("|");
                const first = rows[0] as any;
                const formattedDate =
                  first?.date || first?.created_at || first?.updated_at || "—";

                return (
                  <React.Fragment key={`group-${gi}`}>
                    <tr className="pot-group-header" aria-hidden>
                      <td colSpan={visibleColumns.length}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                          }}
                        >
                          <div
                            className="group-header-badges"
                            aria-hidden
                            role="group"
                            aria-label={`Order badges for ${company} ${orderNo}`}
                            style={{ minWidth: 0 }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                                flexWrap: "wrap",
                                minWidth: 0,
                              }}
                            >
                              {(() => {
                                // Badge descriptors (data-first)
                                const badges: {
                                  key: string;
                                  label: string;
                                  bg: string;
                                  title?: string;
                                }[] = [
                                  {
                                    key: "company",
                                    label: company,
                                    bg: "green",
                                    title: company,
                                  },
                                  {
                                    key: "orderNo",
                                    label: orderNo,
                                    bg: "#f59e0b",
                                    title: orderNo,
                                  },
                                  {
                                    key: "uniqueQuotation",
                                    label: uniqueQuotation,
                                    bg: "#06b6d4",
                                    title: uniqueQuotation,
                                  },
                                  // {
                                  //   key: "date",
                                  //   label: formattedDate,
                                  //   bg: "#2563eb",
                                  //   title: formattedDate,
                                  // },
                                ];

                                // small reusable style factory for badges
                                const badgeStyle = (bg: string) => ({
                                  background: bg,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "4px 8px",
                                  borderRadius: 999,
                                  fontWeight: 600,
                                  fontSize: "0.82rem",
                                  color: "var(--pot-badge-text)",
                                  whiteSpace: "nowrap" as const,
                                });

                                return (
                                  <>
                                    {badges.map((b) => (
                                      <span
                                        key={b.key}
                                        className="pot-badge"
                                        style={badgeStyle(b.bg)}
                                        title={b.title}
                                        aria-hidden="true"
                                      >
                                        {b.label}
                                      </span>
                                    ))}

                                    {/* Action badge preserved as before (keeps original render logic) */}
                                    <span
                                      className="pot-badge"
                                      style={{ padding: "4px 8px" }}
                                    >
                                      {actionCol && actionCol.render
                                        ? actionCol.render(first, 0)
                                        : actionCol
                                        ? first
                                          ? getNestedValue(
                                              first,
                                              String(actionCol.key)
                                            )
                                          : null
                                        : null}
                                    </span>
                                  </>
                                );
                              })()}
                            </div>
                          </div>

                          <div></div>
                        </div>
                      </td>
                    </tr>

                    {rows.map((item, ri) => (
                      <tr key={String(rowKey(item, ri))}>
                        {visibleColumns.map((col, ci) => {
                          const value = renderCellValue(col, item, ri);
                          const html = col.isHtml && typeof value === "string";

                          // tooltip threshold: strings longer than 25 chars.
                          // If you want to test easily, lower this to > 5.
                          const shouldTooltip =
                            !html &&
                            typeof value === "string" &&
                            value.length > 25;

                          const onMouseEnter = (e: React.MouseEvent) => {
                            const target = e.currentTarget as HTMLElement;
                            const rect = target.getBoundingClientRect();
                            showTooltip(
                              String(value),
                              rect,
                              String(value).length > 160
                            );
                          };
                          const onMouseLeave = () => hideTooltip();

                          return (
                            <td
                              key={`cell-${gi}-${ri}-${ci}`}
                              onMouseEnter={
                                shouldTooltip ? onMouseEnter : undefined
                              }
                              onMouseLeave={
                                shouldTooltip ? onMouseLeave : undefined
                              }
                              onFocus={
                                shouldTooltip
                                  ? (e) => {
                                      const target =
                                        e.currentTarget as HTMLElement;
                                      const rect =
                                        target.getBoundingClientRect();
                                      showTooltip(
                                        String(value),
                                        rect,
                                        String(value).length > 160
                                      );
                                    }
                                  : undefined
                              }
                              onBlur={
                                shouldTooltip ? () => hideTooltip() : undefined
                              }
                              title={undefined}
                            >
                              {html ? (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: value as string,
                                  }}
                                />
                              ) : String(col.key) === "password" ? (
                                "********"
                              ) : isImageUrl(value) ? (
                                <img
                                  src={String(value)}
                                  alt=""
                                  style={{
                                    width: 60,
                                    height: 60,
                                    objectFit: "contain",
                                    borderRadius: 6,
                                  }}
                                  onError={(e) =>
                                    (e.currentTarget.style.display = "none")
                                  }
                                />
                              ) : (
                                (value as React.ReactNode)
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Mobile view
  const MobileView = () => (
    <div
      className={`pot-root ${className}`}
      data-theme={theme === "auto" ? undefined : theme}
    >
      {groupKeys.length === 0 ? (
        <div className="pot-empty">{emptyMessage}</div>
      ) : (
        groupKeys.map((gk, gi) => {
          const rows = grouped[gk] || [];
          const [company, orderNo, uniqueQuotation] = gk.split("|");
          const first = rows[0] as any;
          const itemsToShow = rows.slice(0, mobileLimitPerGroup);

          return (
            <div key={`group-mobile-${gi}`} style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}>
                <div
                  className="group-header-badges"
                  aria-hidden
                  role="group"
                  aria-label={`Order badges for ${company} ${orderNo}`}
                  style={{ minWidth: 0 }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      flexWrap: "wrap",
                      minWidth: 0,
                    }}
                  >
                    {(() => {
                      // Badge descriptors (data-first)
                      const badges: {
                        key: string;
                        label: string;
                        bg: string;
                        title?: string;
                      }[] = [
                        {
                          key: "company",
                          label: company,
                          bg: "green",
                          title: company,
                        },
                        {
                          key: "orderNo",
                          label: orderNo,
                          bg: "#f59e0b",
                          title: orderNo,
                        },
                        {
                          key: "uniqueQuotation",
                          label: uniqueQuotation,
                          bg: "#06b6d4",
                          title: uniqueQuotation,
                        },
                        // {
                        //   key: "date",
                        //   label: formattedDate,
                        //   bg: "#2563eb",
                        //   title: formattedDate,
                        // },
                      ];

                      // small reusable style factory for badges
                      const badgeStyle = (bg: string) => ({
                        background: bg,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "4px 8px",
                        borderRadius: 999,
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        color: "var(--pot-badge-text)",
                        whiteSpace: "nowrap" as const,
                      });

                      return (
                        <>
                          {badges.map((b) => (
                            <span
                              key={b.key}
                              className="pot-badge"
                              style={badgeStyle(b.bg)}
                              title={b.title}
                              aria-hidden="true"
                            >
                              {b.label}
                            </span>
                          ))}

                          {/* Action badge preserved as before (keeps original render logic) */}
                          <span
                            className="pot-badge"
                            style={{ padding: "4px 8px" }}
                          >
                            {actionCol && actionCol.render
                              ? actionCol.render(first, 0)
                              : actionCol
                              ? first
                                ? getNestedValue(first, String(actionCol.key))
                                : null
                              : null}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              <div
                className="pot-card-list"
                role="list"
                aria-label={`Items for ${company}`}
              >
                {itemsToShow.map((item, idx) => {
                  const imgCol = columns.find((c) => {
                    const v = renderCellValue(c, item, idx);
                    return typeof v === "string" && isImageUrl(v);
                  });

                  const headerCols: Column<T>[] = [];
                  if (imgCol) headerCols.push(imgCol);
                  const nonActionCols = columns.filter(
                    (c) =>
                      !c.label.toLowerCase().includes("action") &&
                      c !== imgCol &&
                      !isSerialLabel(String(c.label))
                  );
                  for (
                    let i = 0;
                    i < nonActionCols.length && headerCols.length < 2;
                    i++
                  )
                    headerCols.push(nonActionCols[i]);

                  const actionNodes = columns
                    .filter((c) => c.label.toLowerCase().includes("action"))
                    .map((ac) => renderCellValue(ac, item, idx))
                    .filter((n) => n !== undefined && n !== null);

                  const rcCol = columns.find(
                    (c) =>
                      String(c.key).toLowerCase() === "rc" ||
                      c.label.toLowerCase() === "rc"
                  );
                  const factorCol = columns.find(
                    (c) =>
                      String(c.key).toLowerCase() === "factor" ||
                      c.label.toLowerCase() === "factor"
                  );
                  const rcVal = rcCol
                    ? renderCellValue(rcCol, item, idx)
                    : undefined;
                  const factorVal = factorCol
                    ? renderCellValue(factorCol, item, idx)
                    : undefined;

                  return (
                    <div
                      className="pot-card"
                      key={String(rowKey(item, idx))}
                      role="listitem"
                      aria-labelledby={`pot-card-${gi}-${idx}`}
                    >
                      <div className="pot-card-head">
                        <div
                          style={{
                            display: "flex",
                            gap: 12,
                            alignItems: "center",
                            minWidth: 0,
                          }}
                        >
                          {imgCol
                            ? (() => {
                                const img = renderCellValue(
                                  imgCol,
                                  item,
                                  idx
                                ) as string;
                                return (
                                  <div
                                    className="pot-thumb"
                                    title={String(img)}
                                  >
                                    <img
                                      src={img}
                                      alt={imgCol.label}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                      onError={(e) =>
                                        (e.currentTarget.style.display = "none")
                                      }
                                    />
                                  </div>
                                );
                              })()
                            : null}

                          <div style={{ minWidth: 0, overflow: "hidden" }}>
                            {headerCols[0] ? (
                              <>
                                <div
                                  id={`pot-card-${gi}-${idx}`}
                                  className="pot-card-title"
                                >
                                  {headerCols[0].label}
                                </div>
                                <div
                                  className="pot-card-sub"
                                  title={String(
                                    renderCellValue(headerCols[0], item, idx) ??
                                      ""
                                  )}
                                >
                                  {
                                    renderCellValue(
                                      headerCols[0],
                                      item,
                                      idx
                                    ) as React.ReactNode
                                  }
                                </div>
                              </>
                            ) : null}
                            {headerCols[1] ? (
                              <div
                                className="pot-card-sub"
                                style={{ marginTop: 4 }}
                                title={String(
                                  renderCellValue(headerCols[1], item, idx) ??
                                    ""
                                )}
                              >
                                {headerCols[1].label}:{" "}
                                {renderCellValue(headerCols[1], item, idx)}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {(rcVal !== undefined || factorVal !== undefined) && (
                        <div className="pot-rcfactor" aria-hidden>
                          <div>
                            <div className="pot-rc-label">RC</div>
                            <div className="pot-rc-value">{rcVal ?? "—"}</div>
                          </div>
                          <div>
                            <div className="pot-rc-label">Factor</div>
                            <div className="pot-rc-value">
                              {factorVal ?? "—"}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="pot-card-rows">
                        {visibleColumns.map((col, ci) => {
                          const keyStr = col.key.toString();
                          if (
                            headerCols.some((h) => h.key.toString() === keyStr)
                          )
                            return null;
                          if (col.label.toLowerCase().includes("action"))
                            return null;
                          if (isSerialLabel(String(col.label))) return null;
                          const val = renderCellValue(col, item, idx);
                          if (typeof val === "string" && isImageUrl(val))
                            return null;
                          if (val === "" || val === undefined || val === null)
                            return null;

                          const content =
                            col.isHtml && typeof val === "string" ? (
                              <span dangerouslySetInnerHTML={{ __html: val }} />
                            ) : String(col.key) === "password" ? (
                              "********"
                            ) : (
                              (val as React.ReactNode)
                            );

                          const shouldTooltip =
                            typeof val === "string" && val.length > 25;

                          const onMouseEnter = (e: React.MouseEvent) => {
                            const target = e.currentTarget as HTMLElement;
                            const rect = target.getBoundingClientRect();
                            showTooltip(
                              String(val),
                              rect,
                              String(val).length > 160
                            );
                          };
                          const onMouseLeave = () => hideTooltip();

                          return (
                            <div
                              className="pot-row"
                              key={`m-${gi}-${idx}-${ci}`}
                            >
                              <div className="pot-row-label">{col.label}</div>
                              <div
                                className="pot-row-value"
                                onMouseEnter={
                                  shouldTooltip ? onMouseEnter : undefined
                                }
                                onMouseLeave={
                                  shouldTooltip ? onMouseLeave : undefined
                                }
                                onFocus={
                                  shouldTooltip
                                    ? (e) => {
                                        const target =
                                          e.currentTarget as HTMLElement;
                                        const rect =
                                          target.getBoundingClientRect();
                                        showTooltip(
                                          String(val),
                                          rect,
                                          String(val).length > 160
                                        );
                                      }
                                    : undefined
                                }
                                onBlur={
                                  shouldTooltip
                                    ? () => hideTooltip()
                                    : undefined
                                }
                                title={undefined}
                              >
                                {content}
                              </div>
                            </div>
                          );
                        })}

                        {actionNodes.length > 0 && (
                          <div className="pot-row" aria-hidden>
                            <div className="pot-row-label">Actions</div>
                            <div style={{ display: "flex" }}>
                              {actionNodes.map((n, i) => (
                                <span key={`act-${i}`}>
                                  {React.isValidElement(n) ? n : n}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {rows.length > mobileLimitPerGroup && (
                <div
                  style={{
                    marginTop: 8,
                    color: "var(--pot-muted)",
                    fontSize: 13,
                  }}
                >
                  Showing {mobileLimitPerGroup} of {rows.length} items for{" "}
                  {company}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <div
      className="partial-order-table-root"
      data-theme={theme === "auto" ? undefined : theme}
    >
      {isMobile ? <MobileView /> : <DesktopView />}
    </div>
  );
}

export default PartialOrderTable;
